import jwt from 'jsonwebtoken';

import { LOGIN_PATH } from '../config/api';
import Http from '../utils/http';
import { ILoginUser, ICurrentUser } from './../types/AuthTypes';
import { IRoute, LocaleStorageItem, Routes } from './../constants';
import { get } from 'lodash';

const http = new Http();

class AuthService {

  public login(user: ILoginUser): Promise<ICurrentUser> {
    
    const login = user.login;
    const password = user.password;
    return http.post(LOGIN_PATH, { login, password })
      .then(this.handleResponse)
      .then((userToken: string) => {
        
        try {
          const decodedToken: any = jwt.decode(userToken);

          const currentUser: ICurrentUser = {
            userName: login,
            userToken: userToken,
            userRoles: decodedToken.userRoles || [],
            userPermissions: decodedToken.userPermissions || [],
          };

          this.setCurrentUser(currentUser);
          return currentUser;

        } catch (error) {
          throw Error(`An error occurred while logging in. Error: ${error}`);
        }
      }
    );
  }

  public logout() {
    localStorage.removeItem(LocaleStorageItem.USER);
  }

  public getCurrentUser(): ICurrentUser | null {

    const userFromLocaleStorage = localStorage.getItem(LocaleStorageItem.USER) || '';

    if (!userFromLocaleStorage) {
      return null;
    }

    try {
      const currentUser = JSON.parse(userFromLocaleStorage) as ICurrentUser;
      return currentUser;
      
    } catch (error) {
      throw Error(`There is an error while getting current user. Error: ${error}`);
    }
  }

  public userIsInRole(role: string): boolean {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      return currentUser.userRoles.some((userRole: string) => userRole === role );
    }

    return false;
  }

  public userHasPermission(permission: string): boolean {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      return currentUser.userPermissions.some((item: string) => item === permission);
    }

    return false;
  }

  public getCurrentUserRoutes(): IRoute[] {
    const userRoutes = Routes.filter(route => {
      const isPublicRoute = route.accessControl === undefined;
      const roles: [] = get(route, 'accessControl.roles', []);
      const permissions: [] = get(route, 'accessControl.permissions', []);

      const userIsInRole = roles.some(role => this.userIsInRole(role));
      const userHasPermission = permissions.some(permission => this.userHasPermission(permission));
      const userHasAccess = isPublicRoute || userIsInRole || userHasPermission;

      return userHasAccess;
    });

    return userRoutes;
  }

  private setCurrentUser(currentUser: ICurrentUser) {
    if (!currentUser) {
      throw Error(`currentUser is not defined.`);
    } 

    localStorage.setItem(LocaleStorageItem.USER, JSON.stringify(currentUser));    
  }

  private handleResponse(response: Response) {
    return response.text().then((text: string) => {
      if (!response.ok) {
        const error = `${response.status} ${response.statusText}`;
        return Promise.reject(error);           
      }

      return text;
    });
  }
}

export const authService = new AuthService();


// WEBPACK FOOTER //
// ./src/services/AuthService.tsx